import React from 'react';
import styled, { keyframes } from 'styled-components';
import { IQuestReward } from '@typings/quest/questTypes';
import { QuestRewardItem } from './MissionRewards';
import { useQuestBoardSetting } from '@pages/Quest/hooks/useQuestBoardSetting';

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// ─── Overlay / Container ────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ${fadeIn} 0.18s ease;
  padding: 16px;
`;

const Modal = styled.div`
  background: var(--cardBackgroundColor);
  border: 1px solid var(--lineColor);
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  animation: ${slideUp} 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
`;

// ─── Header ─────────────────────────────────────────────────────────────────

const Header = styled.div`
  padding: 20px 20px 0;
  text-align: center;
`;

const Trophy = styled.div`
  font-size: 2.5rem;
  margin-bottom: 8px;
`;

const QuestTitle = styled.div`
  font-size: 0.8rem;
  color: var(--subTextColor);
  margin-bottom: 4px;
`;

const CompletedTitle = styled.h3<{ $result?: 'success' | 'failure' }>`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ $result }) =>
    $result === 'success'
      ? 'var(--successColor, #10b981)'
      : $result === 'failure'
      ? 'var(--errorColor, #ef4444)'
      : 'var(--textColor)'};
  margin: 0 0 16px;
`;

// ─── Random badge ────────────────────────────────────────────────────────────

const RandomBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  margin: 0 20px 4px;
  background: linear-gradient(135deg, var(--primaryColor) 0%, #a855f7 100%);
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.3px;
`;

// ─── Reward list ─────────────────────────────────────────────────────────────

const Divider = styled.div`
  height: 1px;
  background: var(--lineColor);
  margin: 0 20px;
`;

const RewardSection = styled.div`
  padding: 16px 20px;
`;

const RewardLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--subTextColor);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
`;

const RewardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// ─── Footer button ───────────────────────────────────────────────────────────

const Footer = styled.div`
  padding: 0 20px 20px;
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: var(--primaryColor);
  color: var(--textColor);
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  font-family: var(--defaultFontFamily);
  transition: filter 0.15s;

  &:hover {
    filter: brightness(1.08);
  }

  &:active {
    filter: brightness(0.95);
  }
`;

// ─── Props ───────────────────────────────────────────────────────────────────

type Props = {
  questTitle: string;
  givenRewards: IQuestReward[];
  isRandomReward: boolean;
  onClose: () => void;
  /** 확률 판정 결과 (undefined = 확률 시스템 미사용) */
  probabilityResult?: 'success' | 'failure';
};

// ─── Component ───────────────────────────────────────────────────────────────

function RewardReceiptModal({ questTitle, givenRewards, isRandomReward, onClose, probabilityResult }: Props) {
  const { questLabel } = useQuestBoardSetting();
  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Trophy>
            {probabilityResult === 'failure' ? '💔' : '🎉'}
          </Trophy>
          <QuestTitle>{questTitle}</QuestTitle>
          <CompletedTitle $result={probabilityResult}>
            {probabilityResult === 'success'
              ? '✅ 성공!'
              : probabilityResult === 'failure'
              ? '❌ 실패...'
              : `${questLabel} 완료!`}
          </CompletedTitle>
        </Header>

        {isRandomReward && (
          <RandomBanner>
            🎲 랜덤 지급 — 아래 보상이 선택되었습니다
          </RandomBanner>
        )}

        <Divider />

        <RewardSection>
          <RewardLabel>
            {probabilityResult === 'failure' ? '적용된 패널티' : '획득한 보상'}
          </RewardLabel>
          <RewardList>
            {givenRewards.map((reward, idx) => (
              <QuestRewardItem key={idx} reward={reward} />
            ))}
            {givenRewards.length === 0 && (
              <div style={{ color: 'var(--subTextColor)', fontSize: '0.875rem' }}>
                지급된 보상이 없습니다.
              </div>
            )}
          </RewardList>
        </RewardSection>

        <Footer>
          <ConfirmButton onClick={onClose}>확인</ConfirmButton>
        </Footer>
      </Modal>
    </Overlay>
  );
}

export default RewardReceiptModal;
